import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, History, FileText, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { plural } from "@/lib/plural";
import { AuditRow } from "@/components/AuditRow";

const PAGE_SIZE = 50;
const ENTITY_OPTIONS = [
  ["assessment", "Assessment (workflow)"], ["user", "Utenti"], ["organization", "Aziende"],
  ["document", "Documenti"], ["settings", "Impostazioni"], ["topic_weight", "Peso topic"],
  ["pillar_weight", "Pesi pilastri E/S/G"], ["indicator", "Benchmark indicatore"], ["benchmark", "Benchmark cluster"],
];

export function AuditLogTab() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setDebounced(search); setPage(0); }, 350); return () => clearTimeout(t); }, [search]);

  const params = useCallback(() => {
    const p = {};
    if (entityFilter !== "all") p.entity_type = entityFilter;
    if (debounced.trim()) p.q = debounced.trim();
    return p;
  }, [entityFilter, debounced]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/audit-log", { params: { ...params(), limit: PAGE_SIZE, offset: page * PAGE_SIZE } });
      setData(res);
    } finally { setLoading(false); }
  }, [params, page]);
  useEffect(() => { load(); }, [load]);

  async function exportCsv() {
    setExporting(true);
    try {
      const res = await api.get("/audit-log/export.csv", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `viva-esg-cronologia-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Esportazione non riuscita");
    } finally { setExporting(false); }
  }

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  return (
    <Card className="rounded-2xl border-slate-100 shadow-soft overflow-hidden mt-4" data-testid="audit-log-card">
      <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex flex-wrap items-center gap-3">
        <History className="w-5 h-5 text-viva-600" />
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-slate-900">Cronologia modifiche</h3>
          <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="audit-total">
            <Lock className="w-3 h-3" /> {plural(data.total, "evento", "eventi")} · registro in sola scrittura: gli eventi non possono essere modificati né eliminati
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting || data.total === 0} data-testid="export-audit-csv">
          {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} Esporta CSV (tutti)
        </Button>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-audit">
          <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
        </Button>
      </div>

      <div className="px-6 py-4 flex flex-wrap gap-3 items-center border-b border-slate-100">
        <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[260px]" data-testid="audit-entity-filter"><SelectValue placeholder="Tipo entità" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le entità</SelectItem>
            {ENTITY_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Cerca utente, entità o azione…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-[280px]" data-testid="audit-search" />
        <Pager page={page} pages={pages} setPage={setPage} />
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>
      ) : data.items.length === 0 ? (
        <div className="px-6 py-16 text-center text-sm text-slate-400">Nessuna modifica registrata{entityFilter !== "all" ? " per questo filtro" : ""}.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-6 py-3">Data / Ora</th>
                <th className="px-6 py-3">Utente</th>
                <th className="px-6 py-3">Entità</th>
                <th className="px-6 py-3">Riferimento</th>
                <th className="px-6 py-3">Modifiche</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((it) => <AuditRow key={it.id} it={it} />)}
            </tbody>
          </table>
        </div>
      )}
      <div className="px-6 py-3 border-t border-slate-100 flex justify-end"><Pager page={page} pages={pages} setPage={setPage} /></div>
    </Card>
  );
}

function Pager({ page, pages, setPage }) {
  return (
    <div className="ml-auto flex items-center gap-2 text-xs text-slate-500" data-testid="audit-pager">
      <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="audit-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
      <span data-testid="audit-page-label">Pagina {page + 1} di {pages}</span>
      <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} data-testid="audit-next-page"><ChevronRight className="w-4 h-4" /></Button>
    </div>
  );
}
